import React, { useState, useEffect } from 'react';
import { PencilIcon, LockClosedIcon, CheckCircleIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { applySanitiser } from '../functions/textSanitisers';

// Function to format date strings to "Day Month Year" format
const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return dateValue;
    
    // Check if the value looks like a date (YYYY-MM-DD format)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (typeof dateValue === 'string' && dateRegex.test(dateValue)) {
        try {
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            }
        } catch (error) {
            console.error('Error formatting date:', error);
        }
    }
    
    // If it's already a Date object
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        return dateValue.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    }
    
    // Return original value if not a recognizable date
    return dateValue;
};

export default function FormAnswerSummary({ formState, handleEdit, isFormLocked, shouldShowCompletionCard }) {
    // Determine if accordion should be expanded by default
    // Expanded when form is completed, collapsed otherwise
    const [isExpanded, setIsExpanded] = useState(true);

    // Update expansion state when form completion status changes
    useEffect(() => {
        if (shouldShowCompletionCard) {
            setIsExpanded(true);
        }
    }, [shouldShowCompletionCard]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="border-2 border-gray-300 bg-gray-50 rounded-2xl text-left p-6 shadow-md mb-6">
            <button
                type="button"
                aria-expanded={isExpanded}
                aria-controls="answer-summary-content"
                className="w-full flex items-center justify-between cursor-pointer hover:bg-gray-100 rounded-lg p-2 -m-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                onClick={toggleExpanded}
            >
                <div className="flex items-center">
                    <h2 className="text-2xl font-bold mr-3">Answers</h2>                    
                </div>
                <div className="flex items-center">
                    {isFormLocked && (
                        <div className="flex items-center text-gray-600 mr-4">
                            <LockClosedIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                            <span className="font-semibold hidden md:inline">Answers can no longer be edited</span>
                            <span className="font-semibold md:hidden">Locked</span>
                        </div>
                    )}
                    {isExpanded ? (
                        <div className="flex items-center text-sm text-gray-600"><span className="mr-2">Hide</span> <ChevronUpIcon className="w-6 h-6 text-gray-500" aria-hidden="true" /></div>
                    ) : (
                        <div className="flex items-center text-sm text-gray-600"><span className="mr-2">Show</span> <ChevronDownIcon className="w-6 h-6 text-gray-500" aria-hidden="true" /></div>
                    )}
                </div>
            </button>

            {isExpanded && (
              <div id="answer-summary-content" role="region" aria-label="Your submitted answers">
                {formState.submittedAnswers.length === 0 ? (
                    <div className="text-gray-500 text-center py-4">
                        Answer questions to see your responses here.
                    </div>
                ) : (
                    formState.submittedAnswers.map((entry, index) => {
                    // Check if all answers are empty/null/"No answer given"
                    const hasAllEmptyAnswers = !entry.wasSkipped && entry.answers.every(answer =>
                        answer.answer === null ||
                        answer.answer === undefined ||
                        answer.answer === "No answer given" ||
                        (Array.isArray(answer.answer) && answer.answer.length === 0)
                    );

                    // Check if question has at least one valid answer
                    const hasValidAnswers = entry.answers.some(answer =>
                        answer.answer !== null &&
                        answer.answer !== undefined &&
                        answer.answer !== "No answer given" &&
                        !(Array.isArray(answer.answer) && answer.answer.length === 0)
                    );

                    // Determine if this is the last item, to avoid adding a divider after it
                    const isLastItem = index === formState.submittedAnswers.length - 1;

                    return (
                        <div key={entry.questionVarName || index}>
                            <div
                                key={entry.questionVarName || index}
                                role="button"
                                tabIndex={0}
                                aria-label={`Edit answer for ${entry.questionTitle}`}
                                className="p-4 mb-4 fade-in group cursor-pointer hover:bg-amber-100 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                                onClick={() => handleEdit(index)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleEdit(index);
                                    }
                                }}
                            >
                                {/* Existing entry rendering code */}
                                <h3 className="font-bold mb-2 text-lg text-gray-600 flex items-center">
                                    {/* Show different icons based on question status */}
                                    {entry.wasSkipped && (
                                        <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 text-green-500" title="Question skipped" />
                                    )}
                                    {hasAllEmptyAnswers && (
                                        <ClockIcon className="w-5 h-5 mr-2 flex-shrink-0 text-amber-500" title="Question skipped temporarily" />
                                    )}
                                    {hasValidAnswers && (
                                        <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 text-green-500" title="Question answered" />
                                    )}

                                    {entry.questionTitle}


                                    {/* Show lock or edit icon */}
                                    {isFormLocked ? (
                                        <LockClosedIcon className="w-5 h-5 ml-2 text-gray-500" />
                                    ) : (
                                        <PencilIcon className="w-5 h-5 ml-2 text-gray-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                    )}
                                </h3>

                                {/* Show skip message if question was explicitly skipped */}
                                {entry.wasSkipped && (
                                    <div className="mb-3 text-gray-600 flex flex-col md:flex-row md:items-center">
                                        <span className="font-semibold w-full md:w-72 mb-1 md:mb-0">You selected</span>
                                        <span className="text-center opacity-75 border-gray-300 text-violet-800 border shadow-sm font-semibold px-2 py-0.5 rounded-md md:ml-2 inline-block max-w-full break-words">
                                            {entry.skipButtonText || "I'm unable to provide this information"}
                                        </span>
                                    </div>
                                )}

                                {/* Show message for implicit skips (empty answers but not marked as skipped) */}
                                {hasAllEmptyAnswers && (
                                    <div className="mb-3 text-gray-600 flex flex-col md:flex-row md:items-center">
                                        <span className="font-semibold w-full md:w-72 mb-1 md:mb-0">You selected</span>
                                        <span className="text-center opacity-75 bg-violet-900 text-white shadow-sm font-semibold px-2 py-0.5 rounded-md md:ml-2 inline-block max-w-full break-words">
                                            Answer Later
                                        </span>
                                    </div>
                                )}

                                {entry.answers.map((answer, idx) => {
                                    // Apply field-aware sanitisation at render time so the
                                    // patient sees a consistently formatted preview regardless
                                    // of whether the data came from a fresh submit or was
                                    // rehydrated from Firestore on page reload. The raw value
                                    // in formData is never mutated.
                                    const rawValue = answer.answer;
                                    const canSanitise = answer.serviceUserVarName
                                        && typeof rawValue === 'string'
                                        && rawValue !== "No answer given";
                                    const { sanitisedValue, wasSanitised } = canSanitise
                                        ? applySanitiser(answer.serviceUserVarName, rawValue)
                                        : { sanitisedValue: rawValue, wasSanitised: false };

                                    return (
                                    <div key={idx} className="flex flex-col md:flex-row gap-2 md:gap-4 pb-2">
                                        <span className="font-semibold w-full md:w-72 text-gray-600">
                                            {answer.question}
                                        </span>
                                        <div className="text-gray-600 w-full md:w-96 whitespace-pre-wrap">
                                            {sanitisedValue === "No answer given" ? (
                                                "No answer given"
                                            ) : Array.isArray(sanitisedValue) ? (
                                                <ul className="list-disc pl-5">
                                                    {sanitisedValue.map((item, i) => (
                                                        <li key={i}>
                                                            {item.otherValue
                                                                ? <>{formatDateForDisplay(item.value)}: <span className="italic">{'\u201c'}{item.otherValue}{'\u201d'}</span></>
                                                                : formatDateForDisplay(item.value)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : typeof sanitisedValue === 'object' && sanitisedValue !== null ? (
                                                sanitisedValue.otherValue
                                                    ? <>{formatDateForDisplay(sanitisedValue.value)}: <span className="italic">{'\u201c'}{sanitisedValue.otherValue}{'\u201d'}</span></>
                                                    : formatDateForDisplay(sanitisedValue.value)
                                            ) : (
                                                formatDateForDisplay(sanitisedValue)
                                            )}
                                            {wasSanitised && (
                                                <span className="block text-xs text-gray-400 italic mt-0.5">
                                                    Automatically formatted
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                            {/* Add divider except after the last item */}
                            {!isLastItem && (
                                <div className="border-b border-gray-200 my-2 mx-4"></div>
                            )}
                        </div>
                    );
                    })
                )}
              </div>
            )}
        </div>
    );
}